<template>
  <div class="relative" ref="switcher" @keydown.esc="closeAndFocus" @focusout="onFocusOut">
    <button ref="trigger" @click="open = !open" aria-haspopup="listbox" :aria-expanded="open"
      class="flex items-center gap-2 px-3 py-2 rounded-lg text-steel-300 hover:bg-white/10 hover:text-white transition-all">
      <Globe class="w-5 h-5" />
      <!-- Full language name only when the header has room (≥xl); globe+chevron suffice below -->
      <span class="text-sm font-medium hidden xl:inline">{{ currentLabel }}</span>
      <ChevronDown class="w-4 h-4 transition-transform" :class="{ 'rotate-180': open }" />
    </button>
    <div v-show="open" role="listbox" class="absolute end-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-[#e2e8f0] z-50">
      <button v-for="lang in langs" :key="lang.code" @click="switchLang(lang.code)"
        role="option" :aria-current="locale === lang.code ? 'true' : undefined"
        class="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
        :class="locale === lang.code ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]' : 'text-[#4a5568] hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)]'">
        <span class="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold" :class="lang.bg">{{ lang.flag }}</span>
        <span>{{ $t(lang.labelKey) }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Globe, ChevronDown } from 'lucide-vue-next'

const { locale, t } = useI18n()
const open = ref(false)
const switcher = ref(null)
const trigger = ref(null)

// flags for zh/ar written as unicode escapes so the source file stays ASCII
const langs = [
  { code: 'zh-CN', labelKey: 'langZh', flag: '\u4e2d', bg: 'bg-red-500' },
  { code: 'en', labelKey: 'langEn', flag: 'EN', bg: 'bg-blue-600' },
  { code: 'fr', labelKey: 'langFr', flag: 'FR', bg: 'bg-indigo-500' },
  { code: 'de', labelKey: 'langDe', flag: 'DE', bg: 'bg-zinc-700' },
  { code: 'it', labelKey: 'langIt', flag: 'IT', bg: 'bg-green-700' },
  { code: 'es', labelKey: 'langEs', flag: 'ES', bg: 'bg-amber-600' },
  { code: 'tr', labelKey: 'langTr', flag: 'TR', bg: 'bg-red-700' },
  { code: 'ar', labelKey: 'langAr', flag: '\u0639', bg: 'bg-green-600' }
]

const currentLabel = computed(() => t(langs.find(l => l.code === locale.value)?.labelKey || 'langEn'))

function switchLang(code) {
  locale.value = code
  open.value = false
}

function closeAndFocus() {
  open.value = false
  trigger.value?.focus()
}

function onFocusOut(e) {
  // Close once focus moves outside the whole switcher (button + dropdown),
  // e.g. Tab past the last language option.
  if (switcher.value && !switcher.value.contains(e.relatedTarget)) open.value = false
}

function onClickOutside(e) {
  if (switcher.value && !switcher.value.contains(e.target)) open.value = false
}

onMounted(() => document.addEventListener('click', onClickOutside))
onUnmounted(() => document.removeEventListener('click', onClickOutside))
</script>
